import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  LinearProgress,
  Tooltip,
  Stack,
  Divider,
  Chip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import MicNoneIcon from "@mui/icons-material/MicNone";
import ScreenShareOutlinedIcon from "@mui/icons-material/ScreenShareOutlined";
import WifiOutlinedIcon from "@mui/icons-material/WifiOutlined";
import DevicesOutlinedIcon from "@mui/icons-material/DevicesOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";

import { logo, WhoWeAreImgpowerd } from "../../Common/CommonImages";
import { PeripheralDevicesDisplay } from "../../Components/PeripheralDetection/PeripheralDevicesDisplay";
import {
  startPeripheralDetection,
  recheckPeripheralDevices,
} from "../../store/Peripheral/action";
import { startCamera } from "../../store/Camera/action";
import { askScreenShare } from "../../store/Screen/actions";
import { validateCamerasStrict } from "../../utils/camera.util";
import { networkSpeedChecker } from "../../utils/networkSpeedChecker";
import { isMobileDevice } from "../../utils/deviceDetection.util";
import AssessmentHeader from "../../Components/AssessmentHeader";

// ── Step Bar (matches Summary / EnterDetails) ─────────────────────────────────


// ── Check Item Colors ─────────────────────────────────────────────────────────
const CHECK_META = {
  camera: {
    icon: <VideocamOutlinedIcon />,
    label: "Camera",
    color: "#1976d2",
    hint: "Click to re-check",
  },
  microphone: {
    icon: <MicNoneIcon />,
    label: "Microphone",
    color: "#7b1fa2",
    hint: "Click to re-check",
  },
  network: {
    icon: <WifiOutlinedIcon />,
    label: "Network Connection",
    color: "#00897b",
    hint: "Click to re-check",
  },
  screenShare: {
    icon: <ScreenShareOutlinedIcon />,
    label: "Screen Sharing",
    color: "#e65100",
    hint: "Click to re-check",
  },
  peripheral: {
    icon: <DevicesOutlinedIcon />,
    label: "Peripheral Devices",
    color: "#6a1b9a",
    hint: "Click to re-check",
  },
};

// ── Status badge ──────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  if (status === "success")
    return <CheckCircleIcon sx={{ fontSize: 26, color: "#4caf50" }} />;
  if (status === "error")
    return <ErrorIcon sx={{ fontSize: 26, color: "#f44336" }} />;
  if (status === "warning")
    return <WarningAmberIcon sx={{ fontSize: 26, color: "#ff9800" }} />;
  if (status === "skipped")
    return <CheckCircleIcon sx={{ fontSize: 26, color: "#bdbdbd" }} />;
  if (status === "checking")
    return <CircularProgress size={24} sx={{ color: "#FF6812" }} />;
  // pending
  return (
    <CircularProgress
      size={24}
      sx={{ color: "#e0e0e0" }}
      variant="determinate"
      value={0}
    />
  );
}

// ── Check Row ─────────────────────────────────────────────────────────────────
function CheckRow({ checkKey, check, onClick }) {
  const meta = CHECK_META[checkKey];
  const isActive = check.status === "checking";
  const isSuccess = check.status === "success";
  const isError = check.status === "error";
  const isWarning = check.status === "warning";

  const borderColor = isSuccess
    ? "#4caf5040"
    : isError
      ? "#f4433630"
      : isWarning
        ? "#ff980030"
        : "#e0e0e0";

  const bgColor = isSuccess
    ? "#f0faf8"
    : isError
      ? "#fff5f5"
      : isWarning
        ? "#fffbf0"
        : isActive
          ? `${meta.color}08`
          : "#fafafa";

  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: { base: 1.5, xss: 2 },
        p: { base: "12px", xss: "14px 16px" },
        borderRadius: "10px",
        border: `1.5px solid ${borderColor}`,
        bgcolor: bgColor,
        cursor: "pointer",
        transition: "all 0.2s",
        "&:hover": {
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          transform: "translateY(-1px)",
        },
      }}
    >
      {/* Icon box */}
      <Box
        sx={{
          width: { base: 36, xss: 42 },
          height: { base: 36, xss: 42 },
          borderRadius: "10px",
          bgcolor: `${meta.color}15`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {React.cloneElement(meta.icon, {
          sx: { fontSize: { base: 18, xss: 20 }, color: meta.color },
        })}
      </Box>

      {/* Text */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={0.2}>
          <Typography
            sx={{
              fontSize: { base: "13px", xss: "14px" },
              fontWeight: 700,
              color: "#1a1a2e",
            }}
          >
            {meta.label}
          </Typography>
          {isActive && (
            <Chip
              label="Checking…"
              size="small"
              sx={{
                height: 18,
                fontSize: "10px",
                bgcolor: `${meta.color}15`,
                color: meta.color,
                fontWeight: 600,
              }}
            />
          )}
        </Stack>
        <Typography
          sx={{
            fontSize: { base: "11px", xss: "12px" },
            color: isError ? "#f44336" : isWarning ? "#e65100" : "#777",
            lineHeight: 1.4,
          }}
        >
          {check.message || "Waiting…"}
        </Typography>
        {isError && checkKey === "screenShare" && (
          <Typography
            sx={{
              fontSize: "11px",
              color: "#f44336",
              fontWeight: 600,
              mt: 0.3,
            }}
          >
            NOTE: You must share your ENTIRE SCREEN. Window or Tab sharing is
            prohibited.
          </Typography>
        )}
        {isError && checkKey === "peripheral" && (
          <Typography sx={{ fontSize: "11px", color: "#f44336", mt: 0.3 }}>
            Please disconnect any secondary monitors or external displays.
          </Typography>
        )}
      </Box>

      {/* Status icon */}
      <StatusBadge status={check.status} />
    </Box>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
function CheckSystem() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const testSettings = useSelector((state) => state.testReducer.setting);
  const peripheralState = useSelector((state) => state.peripheralReducer);
  const cameraState = useSelector((state) => state.cameraReducer);
  const screenState = useSelector((state) => state.screenReducer);

  const {
    hasViolatingDevices,
    detectedDevices,
    peripheralError,
    isCheckingDevices,
  } = peripheralState;

  const [isInitializing, setIsInitializing] = useState(true);
  const [localStream, setLocalStream] = useState(null);
  const [checks, setChecks] = useState({
    camera: { status: "pending", message: "" },
    microphone: { status: "pending", message: "" },
    network: { status: "pending", message: "" },
    screenShare: { status: "pending", message: "" },
    peripheral: { status: "pending", message: "" },
  });
  const [progress, setProgress] = useState(0);

  const videoRef = useRef(null);

  const isPeripheralRestrictionRequired = testSettings?.some(
    (s) => s.setting === "Restrict peripheral device usage" && s.active,
  );
  const isScreenSharingRequired =
    testSettings?.some(
      (s) => s.setting === "Take screen snapshots on tab switch" && s.active,
    ) && !isMobileDevice();

  useEffect(() => {
    if (localStream && videoRef.current) {
      videoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  const checkCamera = async () => {
    try {
      setChecks((prev) => ({
        ...prev,
        camera: { status: "checking", message: "Checking camera…" },
      }));
      try {
        const s = await navigator.mediaDevices.getUserMedia({ video: true });
        s.getTracks().forEach((t) => t.stop());
      } catch (e) {}
      await validateCamerasStrict();
      const stream = await dispatch(startCamera());
      if (stream && stream.active) {
        setLocalStream(stream);
        setChecks((prev) => ({
          ...prev,
          camera: { status: "success", message: "Camera is working" },
        }));
        return true;
      } else {
        const msg =
          cameraState.cameraError?.name === "NotAllowedError"
            ? "Camera permission denied. Please allow camera access in your browser settings."
            : cameraState.cameraError?.message || "Camera access denied";
        setChecks((prev) => ({
          ...prev,
          camera: { status: "error", message: msg },
        }));
        return false;
      }
    } catch (error) {
      const msg =
        error.name === "NotAllowedError" || error.message.includes("permission")
          ? "Camera permission denied. Please enable camera access."
          : error.message;
      setChecks((prev) => ({
        ...prev,
        camera: { status: "error", message: msg },
      }));
      return false;
    }
  };

  const checkMicrophone = async () => {
    try {
      setChecks((prev) => ({
        ...prev,
        microphone: { status: "checking", message: "Checking microphone…" },
      }));
      if (!navigator.mediaDevices?.getUserMedia)
        throw new Error("Microphone access not available");
      const testStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      let canRecord = false;
      try {
        const mr = new MediaRecorder(testStream, {
          mimeType: "audio/webm; codecs=opus",
        });
        canRecord = mr.state === "inactive";
        mr.start();
        setTimeout(() => {
          if (mr.state === "recording") mr.stop();
        }, 100);
      } catch (e) {
        canRecord = false;
      }
      testStream.getTracks().forEach((t) => t.stop());
      if (!canRecord) throw new Error("MICROPHONE_IN_USE");
      setChecks((prev) => ({
        ...prev,
        microphone: { status: "success", message: "Microphone is working" },
      }));
      return true;
    } catch (error) {
      const msg =
        ["NotAllowedError", "NotReadableError"].includes(error.name) ||
        error.message === "MICROPHONE_IN_USE" ||
        error.message?.toLowerCase().includes("in use")
          ? "Microphone is in use by another application. Please close Zoom/Teams/Meet and try again."
          : "Microphone access denied or not available";
      setChecks((prev) => ({
        ...prev,
        microphone: { status: "error", message: msg },
      }));
      return false;
    }
  };

  const checkNetwork = async () => {
    try {
      setChecks((prev) => ({
        ...prev,
        network: { status: "checking", message: "Testing network speed…" },
      }));
      const results = await networkSpeedChecker.performSpeedTest("video", {
        testUpload: true,
        testDownload: true,
        useSimpleFallback: true,
      });
      if (results.overall) {
        setChecks((prev) => ({
          ...prev,
          network: {
            status: "success",
            message: `Good connection (↑ ${results.upload.speedMbps.toFixed(2)} Mbps · ↓ ${results.download.speedMbps.toFixed(2)} Mbps)`,
          },
        }));
        return true;
      } else {
        setChecks((prev) => ({
          ...prev,
          network: {
            status: "warning",
            message:
              results.validation.message || "Network speed may be insufficient",
          },
        }));
        return true;
      }
    } catch (error) {
      try {
        const t0 = Date.now();
        await fetch("https://www.google.com/favicon.ico?" + Date.now(), {
          mode: "no-cors",
        });
        const latency = Date.now() - t0;
        if (latency < 1000) {
          setChecks((prev) => ({
            ...prev,
            network: {
              status: "warning",
              message: `Basic connection OK (${latency}ms) — Speed test failed`,
            },
          }));
          return true;
        }
      } catch (e) {}
      setChecks((prev) => ({
        ...prev,
        network: {
          status: "error",
          message: "No internet connection or unstable network",
        },
      }));
      return false;
    }
  };

  const checkScreenShare = async () => {
    // Skip screen share check on mobile devices
    if (isMobileDevice()) {
      setChecks((prev) => ({
        ...prev,
        screenShare: {
          status: "success",
          message: "Screen sharing skipped on mobile device",
        },
      }));
      return true;
    }

    try {
      setChecks((prev) => ({
        ...prev,
        screenShare: {
          status: "checking",
          message: "Requesting screen share…",
        },
      }));
      if (screenState.isScreenShared && screenState.screenStream?.active) {
        setChecks((prev) => ({
          ...prev,
          screenShare: {
            status: "success",
            message: "Screen sharing is already active",
          },
        }));
        return true;
      }
      const success = await dispatch(askScreenShare());
      if (success) {
        setChecks((prev) => ({
          ...prev,
          screenShare: {
            status: "success",
            message: "Screen sharing is working",
          },
        }));
        return true;
      } else {
        setChecks((prev) => ({
          ...prev,
          screenShare: {
            status: "error",
            message: "Screen sharing permission denied",
          },
        }));
        return false;
      }
    } catch (error) {
      setChecks((prev) => ({
        ...prev,
        screenShare: { status: "error", message: error.message },
      }));
      return false;
    }
  };

  const checkPeripherals = async () => {
    try {
      setChecks((prev) => ({
        ...prev,
        peripheral: {
          status: "checking",
          message: "Scanning for external devices…",
        },
      }));
      const success = await dispatch(startPeripheralDetection());
      if (success) {
        setChecks((prev) => ({
          ...prev,
          peripheral: {
            status: "success",
            message: "No external devices detected",
          },
        }));
        return true;
      } else {
        setChecks((prev) => ({
          ...prev,
          peripheral: { status: "error", message: "External devices detected" },
        }));
        return false;
      }
    } catch (error) {
      setChecks((prev) => ({
        ...prev,
        peripheral: { status: "error", message: error.message },
      }));
      return false;
    }
  };

  useEffect(() => {
    const runAllChecks = async () => {
      setIsInitializing(true);
      setProgress(0);
      const total =
        3 +
        (isScreenSharingRequired ? 1 : 0) +
        (isPeripheralRestrictionRequired ? 1 : 0);
      let done = 0;

      const camOk = await checkCamera();
      setProgress((++done / total) * 100);

      if (camOk) {
        await checkMicrophone();
      } else {
        setChecks((prev) => ({
          ...prev,
          microphone: {
            status: "error",
            message: "Skipped due to camera error",
          },
        }));
      }
      setProgress((++done / total) * 100);

      await checkNetwork();
      setProgress((++done / total) * 100);

      if (isScreenSharingRequired) {
        await checkScreenShare();
        setProgress((++done / total) * 100);
      } else {
        setChecks((prev) => ({
          ...prev,
          screenShare: {
            status: "skipped",
            message: "Not required for this test",
          },
        }));
      }

      if (isPeripheralRestrictionRequired) {
        await checkPeripherals();
        setProgress((++done / total) * 100);
      } else {
        setChecks((prev) => ({
          ...prev,
          peripheral: {
            status: "skipped",
            message: "Not required for this test",
          },
        }));
      }

      setIsInitializing(false);
    };
    runAllChecks();
  }, [dispatch, isPeripheralRestrictionRequired, isScreenSharingRequired]);

  const handleReCheck = async () => {
    const success = await dispatch(recheckPeripheralDevices());
    if (success)
      setChecks((prev) => ({
        ...prev,
        peripheral: {
          status: "success",
          message: "No external devices detected",
        },
      }));
  };

  const handleRequestPermissions = async () => {
    await checkPeripherals();
  };
  const handleContinue = () => navigate("/start-test");
  const handleBack = () => navigate("/enter-details");

  const canProceed = () => {
    if (checks.camera.status !== "success") return false;
    if (checks.microphone.status !== "success") return false;
    if (checks.network.status === "error") return false;
    if (isScreenSharingRequired && checks.screenShare.status !== "success")
      return false;
    if (
      isPeripheralRestrictionRequired &&
      checks.peripheral.status !== "success"
    )
      return false;
    return true;
  };

  // Count completed checks for summary
  const completedCount = Object.values(checks).filter(
    (c) => c.status === "success" || c.status === "skipped",
  ).length;
  const totalCount =
    Object.values(checks).filter((c) => c.status !== "pending").length || 5;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f5f7fa",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ── Unified Header ── */}
      <AssessmentHeader activeStep="System Check" hasSystemCheck={isPeripheralRestrictionRequired} />

      {/* ── Main Content ── */}
      <Box
        sx={{
          flex: 1,
          py: { base: 2 },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: { base: "95%", sm: "75%" },
            mx: "auto",
          }}
        >
          {/* ── Card ── */}
          <Box
            sx={{
              bgcolor: "#fff",
              borderRadius: "12px",
              boxShadow: "0 2px 20px rgba(0,0,0,0.07)",
              overflow: "hidden",
            }}
          >
            {/* Card Header */}
            <Box
              sx={{
                padding: {
                  base: "14px 16px",
                  xss: "16px 20px",
                  sm: "20px 24px",
                },
                background: "linear-gradient(135deg, #fff 60%, #fff8f4 100%)",
              }}
            >
              <Stack
                direction={{ base: "column", xss: "row" }}
                alignItems={{ base: "flex-start", xss: "center" }}
                justifyContent="space-between"
                spacing={2}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: "12px",
                      background: "linear-gradient(135deg, #1565c0, #42a5f5)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      boxShadow: "0 3px 10px rgba(21,101,192,0.3)",
                    }}
                  >
                    <MonitorHeartOutlinedIcon
                      sx={{ fontSize: 24, color: "#fff" }}
                    />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: { base: "16px", xss: "17px", sm: "19px" },
                        fontWeight: 700,
                        color: "#1a1a2e",
                        lineHeight: 1.3,
                        mb: 0.2,
                      }}
                    >
                      System Check
                    </Typography>
                    <Typography sx={{ fontSize: "13px", color: "#888" }}>
                      Verifying your setup before the assessment begins
                    </Typography>
                  </Box>
                </Stack>

                {/* Progress summary badge */}
                {!isInitializing && (
                  <Chip
                    icon={
                      <CheckCircleIcon
                        sx={{
                          fontSize: "16px !important",
                          color: canProceed()
                            ? "#4caf50 !important"
                            : "#FF6812 !important",
                        }}
                      />
                    }
                    label={
                      canProceed()
                        ? "All checks passed"
                        : `${completedCount} / ${totalCount} passed`
                    }
                    sx={{
                      bgcolor: canProceed() ? "#f0faf8" : "#fff8f4",
                      border: `1px solid ${canProceed() ? "#b2dfdb" : "#ffd4b8"}`,
                      color: canProceed() ? "#00695c" : "#FF6812",
                      fontWeight: 700,
                      fontSize: "12px",
                      height: 32,
                      "& .MuiChip-icon": { ml: 1 },
                    }}
                  />
                )}
              </Stack>
            </Box>

            <Divider sx={{ borderColor: "#bdbdbd" }} />

            {/* Progress Bar */}
            {isInitializing && (
              <Box
                sx={{
                  px: { base: "16px", xss: "24px" },
                  pt: "16px",
                  pb: "8px",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={0.8}
                >
                  <Typography
                    sx={{ fontSize: "12px", color: "#888", fontWeight: 500 }}
                  >
                    Running system checks…
                  </Typography>
                  <Typography
                    sx={{ fontSize: "12px", color: "#FF6812", fontWeight: 700 }}
                  >
                    {Math.round(progress)}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    height: 6,
                    borderRadius: 4,
                    bgcolor: "#f0f0f0",
                    "& .MuiLinearProgress-bar": {
                      background: "linear-gradient(90deg, #FF6812, #ff9a5c)",
                      borderRadius: 4,
                    },
                  }}
                />
              </Box>
            )}

            {/* Check Items */}
            <Box sx={{ padding: { base: "16px", xss: "20px 24px" } }}>
              <Stack spacing={1.5}>
                <CheckRow
                  checkKey="camera"
                  check={checks.camera}
                  onClick={checkCamera}
                />
                <CheckRow
                  checkKey="microphone"
                  check={checks.microphone}
                  onClick={checkMicrophone}
                />
                <CheckRow
                  checkKey="network"
                  check={checks.network}
                  onClick={checkNetwork}
                />
                {isScreenSharingRequired && (
                  <CheckRow
                    checkKey="screenShare"
                    check={checks.screenShare}
                    onClick={checkScreenShare}
                  />
                )}
                {isPeripheralRestrictionRequired && (
                  <CheckRow
                    checkKey="peripheral"
                    check={checks.peripheral}
                    onClick={checkPeripherals}
                  />
                )}
              </Stack>

              {/* Peripheral violations display */}
              {isPeripheralRestrictionRequired && (
                <Box sx={{ mt: 2 }}>
                  <PeripheralDevicesDisplay
                    hasViolatingDevices={hasViolatingDevices}
                    detectedDevices={detectedDevices}
                    peripheralError={peripheralError}
                    isCheckingDevices={isCheckingDevices}
                    isPeripheralRestrictionRequired={
                      isPeripheralRestrictionRequired
                    }
                    onReCheck={handleReCheck}
                    onRequestPermissions={handleRequestPermissions}
                  />
                </Box>
              )}

              {/* Helper text when blocked */}
              {!isInitializing && !canProceed() && (
                <Box
                  sx={{
                    mt: 2,
                    p: "10px 14px",
                    bgcolor: "#fff8f4",
                    border: "1px solid #ffd4b8",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <WarningAmberIcon
                    sx={{ fontSize: 16, color: "#FF6812", flexShrink: 0 }}
                  />
                  <Typography sx={{ fontSize: "12px", color: "#e65100" }}>
                    Please resolve all issues above before continuing. Click on
                    any check item to retry.
                  </Typography>
                </Box>
              )}
            </Box>

            <Divider sx={{ borderColor: "#bdbdbd" }} />

            {/* Footer Actions */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { base: "column", xss: "row" },
                justifyContent: "space-between",
                alignItems: "center",
                px: { base: "16px", xss: "24px" },
                py: "16px",
                gap: { base: 2, xss: 0 },
              }}
            >
              <Button
                onClick={handleBack}
                startIcon={<ArrowBackIcon />}
                sx={{
                  height: 40,
                  border: "1.5px solid #e0e0e0",
                  color: "#555",
                  fontSize: "13px",
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: "10px",
                  px: 3,
                  width: { base: "100%", xss: "auto" },
                  "&:hover": {
                    borderColor: "#FF6812",
                    color: "#FF6812",
                    bgcolor: "#fff8f4",
                  },
                  transition: "all 0.2s",
                }}
              >
                Back
              </Button>

              <Tooltip
                title={
                  !canProceed() && !isInitializing
                    ? "Please resolve all system check issues to continue"
                    : ""
                }
                arrow
              >
                <span>
                  <Button
                    onClick={handleContinue}
                    disabled={isInitializing || !canProceed()}
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      background: "linear-gradient(135deg, #FF6812, #ff9a5c)",
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: { base: "14px", xss: "15px" },
                      height: 42,
                      borderRadius: "10px",
                      textTransform: "none",
                      boxShadow: "0 4px 16px rgba(255,104,18,0.35)",
                      width: { base: "100%", xss: "auto" },
                      px: { xss: 4 },
                      "&:hover": {
                        background: "linear-gradient(135deg, #e55a0f, #ff8533)",
                        boxShadow: "0 6px 20px rgba(255,104,18,0.45)",
                        transform: "translateY(-1px)",
                      },
                      "&.Mui-disabled": {
                        background: "#e0e0e0",
                        color: "#aaa",
                        boxShadow: "none",
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    {isInitializing ? "Checking…" : "Proceed to Test"}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>

          {/* Footer */}
          <Box sx={{ mt: 4, mb: 2, display: "flex", justifyContent: "center" }}>
            <img
              src={WhoWeAreImgpowerd}
              alt="Powered by HireCorrecto"
              style={{ height: 28, width: 200 }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default CheckSystem;
